// When users sign in and out, we want to have a nice transition rather
// than simply replacing state. The function of unloadPromise is to supply
// some interface where all the different aspects can signal themselves as
// being ready before the whole app is done

//  Ideal functionality
//
//  ┌─── User status change, blockForUserLoad set true
//  │                                             ┌─── Weakest link is ready, initialise new user state retrieval all
//  │                                             │    three are notified and so we allow the loading procedure to start
//  │ <--- UNLOADING ----------->                 │ <--- LOADING ---->
//  Profile .... (rdy / notify)                   │ Profile..
//  Tabs ..... (rdy / notify)                     │ Tabs..
//  Highlights .................... (rdy / notify)│ Highlights..
//
//  ex: 0s      1s      2s      3s      4s      5s      6s ... etc
//  [Show loader] .............................................. [Hide loader]

import { type UserStatus, userStatus } from "@utils/underhood/firebase"
import { writable } from "svelte/store"

// This is set to false while we're waiting for everything to load
export const blockForUserLoad = writable(true)

// IMPORTANT! If you add a new thing to load, make sure you increment this
// Currently: highlights, tabs, profile, deviceSettings, lesson progress
const LOAD_COUNT = 5

// Each user branch needs to tag that they're unloaded before they should access the rest
// Using generics we can pass certain info generated in unload to load, if necessary
type RegistryProfile<T> = {
    /**
     * Begin the unloading sequence
     * @returns
     */
    unload: () => Promise<T>
    /**
     *
     * @param status Undetermined will never be passed
     * @param pass The object unique to the loader to pass
     * @returns
     */
    load: (
        status: Exclude<UserStatus, "undetermined">,
        pass: T
    ) => Promise<unknown>
}
const registryProfiles: RegistryProfile<any>[] = []

/**
 * Registers a user status callback. Unload will be called, and when all of the registry profiles have successfully
 * unloaded, then load will be called. Info from unload function can be passed in the promise return, and will be
 * accessible to the load function via the second argument
 *
 * **IMPORTANT: Make sure you increment `LOAD_COUNT` in this file if you add another one**
 *
 * @param obj
 * @param obj.unload
 * @param obj.load Function that returns a promise to be called to initiate next loading sequence
 */
export function registerUserStatusCallback<T>(profile: RegistryProfile<T>) {
    registryProfiles.push(profile)
    if (registryProfiles.length == LOAD_COUNT) {
        console.log(
            `⚙️ ${LOAD_COUNT}/${LOAD_COUNT} User Status Listeners loaded`
        )
        init()
    }
    if (registryProfiles.length > LOAD_COUNT) {
        console.error("Uh oh!! Looks like we have an extra user listener")
    }
}

// Received a new status change
// What happens if status is cancelled half way?
// Good question!
// Really? Thanks.
// What you need to do is set a little variable to track the identities
let currentLoader = 0
function init() {
    userStatus.subscribe(async (status) => {
        currentLoader++
        const thisLoader = currentLoader

        // TODO Set this properly
        if (status == "undetermined") return

        // Show loading screen
        blockForUserLoad.set(true)

        // Call each unload function
        const returns = await Promise.all(
            registryProfiles.map((profile) => profile.unload())
        )

        // This is the simple substitution for the big workaround below
        await new Promise((res) => setTimeout(res, 1000))

        if (thisLoader !== currentLoader) {
            // console.warn(`User loader ${status} with id ${currentLoader} aborted`)
            return
        }
        // Call each load function
        await Promise.all(
            registryProfiles.map((profile) =>
                profile.load(status, returns.shift())
            )
        )

        // Unblock loading screen
        blockForUserLoad.set(false)
    })
}
